import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { ExternalLink } from "lucide-react";

function useCountUp(target, duration = 2.2) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (!inView) return;
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) frame = requestAnimationFrame(tick);
      else setCount(target);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [inView, target, duration]);

  return { count, ref };
}

const TIERS = [
  {
    label: "People alive on Earth",
    value: 8100000000,
    display: "8.1 Billion",
    sub: "Every human being alive today",
    width: "100%",
    delay: 0,
  },
  {
    label: "Centenarians worldwide",
    value: 600000,
    display: "~600,000",
    sub: "Those who have reached 100",
    width: "40%",
    delay: 0.15,
  },
  {
    label: "Estimated to reach 105",
    value: 35000,
    display: "~35,000",
    sub: "Five more years beyond a century",
    width: "22%",
    delay: 0.3,
  },
  {
    label: "Reach Agnes's age of 107",
    value: 600,
    display: "~600",
    sub: "The rarest of the rare",
    width: "9%",
    delay: 0.45,
    highlight: true,
  },
];

function StatBar({ tier, index }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: -30 }}
      animate={inView ? { opacity: 1, x: 0 } : {}}
      transition={{ duration: 0.6, delay: tier.delay, ease: [0.22, 1, 0.36, 1] }}
      className="flex items-center gap-4"
    >
      <div className="w-36 shrink-0 text-right">
        <span className={`font-serif text-lg font-bold leading-tight ${tier.highlight ? "text-gold" : "text-cream/90"}`}>
          {tier.display}
        </span>
      </div>
      <div className="flex-1">
        <div className="relative h-10 rounded-full bg-white/5 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={inView ? { width: tier.width } : { width: 0 }}
            transition={{ duration: 1.1, delay: tier.delay + 0.2, ease: [0.22, 1, 0.36, 1] }}
            className={`h-full rounded-full ${tier.highlight
              ? "bg-gradient-to-r from-gold to-yellow-300 shadow-[0_0_20px_rgba(212,175,55,0.5)]"
              : "bg-gradient-to-r from-cream/30 to-cream/10"
              }`}
          />
        </div>
        <p className="mt-1 text-xs text-cream/50 pl-1">{tier.sub}</p>
      </div>
    </motion.div>
  );
}

function OddsCard() {
  const { count, ref } = useCountUp(13500000, 2.5);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl border border-gold/30 bg-white/5 p-8 text-center backdrop-blur-sm"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-gold/5 to-transparent" />
      <p className="relative text-sm uppercase tracking-[0.3em] text-gold/70 mb-2">The odds</p>
      <p className="relative font-serif text-7xl font-bold text-gold leading-none">
        1 in {count.toLocaleString()}
      </p>
      <p className="relative mt-3 text-cream/60 text-sm max-w-xs mx-auto">
        Estimated chance of any person alive today reaching Agnes's age
      </p>
    </motion.div>
  );
}

export default function RaritySection() {
  return (
    <section className="relative overflow-hidden bg-forest grain py-24 md:py-32">
      <div className="absolute inset-0 bg-gradient-to-b from-forest via-forest to-[#1a2614]" />

      <div className="relative mx-auto max-w-5xl px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.35em] text-gold/70">By the numbers</span>
          <h2 className="mt-4 font-serif text-5xl font-bold tracking-tight text-cream md:text-6xl">
            Beyond <span className="italic text-gold">Rare</span>
          </h2>
          <p className="mt-5 max-w-xl mx-auto text-cream/65 leading-relaxed md:text-lg">
            Reaching 100 is extraordinary. What Agnes has achieved goes far beyond that —
            placing her among the most remarkable humans who have ever lived.
          </p>
        </motion.div>

        <div className="grid gap-16 md:grid-cols-2 md:gap-12 items-center mb-16">
          <div className="space-y-6">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-xs uppercase tracking-[0.3em] text-gold/60 mb-8"
            >
              Of every person alive today…
            </motion.p>
            {TIERS.map((tier, i) => (
              <StatBar key={tier.label} tier={tier} index={i} />
            ))}
            <p className="text-xs text-cream/30 pt-2 pl-40">
              * Estimates based on UN & demographic research data
            </p>
          </div>

          <div className="space-y-6">
            <OddsCard />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="rounded-2xl border border-cream/10 bg-white/4 p-6 backdrop-blur-sm"
            >
              <p className="font-serif text-xl italic text-cream/80 leading-relaxed">
                "Agnes is not just old — she is a living scientific wonder, 
                belonging to a group smaller than most small towns."
              </p>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          className="rounded-3xl border border-gold/25 bg-white/5 p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6 backdrop-blur-sm"
        >
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.3em] text-gold/70 mb-2">Official Record</p>
            <h3 className="font-serif text-2xl font-bold text-cream">
              Gerontology Research Group
            </h3>
            <p className="mt-2 text-cream/60 leading-relaxed text-sm md:text-base max-w-lg">
              The GRG maintains the world's only scientifically verified list of living 
              supercentenarians — people aged 110 and over. At 107, Agnes is just three 
              years from this ultra-exclusive roster of history's longest-lived humans.
            </p>
          </div>
          <div className="shrink-0">
            <a
              href="https://www.grg-supercentenarians.org/world-supercentenarian-rankings-list/"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 rounded-full border border-gold/50 bg-gold/10 px-7 py-3.5 font-medium text-gold transition hover:bg-gold hover:text-forest"
            >
              View the GRG list
              <ExternalLink className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
